import React from "react";

import {
  Box,
  ButtonBase,
  Dialog,
  IconButton,
  LinearProgress,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { fade, makeStyles, useTheme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "min(1180px, calc(100vw - 48px))",
    height: "min(780px, calc(100vh - 48px))",
    maxWidth: "none",
    maxHeight: "none",
    minHeight: 0,
    margin: theme.spacing(3),
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      height: "100%",
      margin: 0,
      borderRadius: 0,
    },
  },
  shell: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    position: "relative",
    zIndex: 2,
    display: "grid",
    gridTemplateColumns: "minmax(220px, 1fr) auto auto",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "minmax(0, 1fr) auto",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5),
    },
  },
  identity: {
    minWidth: 0,
  },
  eyebrow: {
    color: theme.palette.text.secondary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  title: {
    fontWeight: 600,
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  identityMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
    marginTop: theme.spacing(0.75),
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      gridColumn: "1 / -1",
      gridRow: 2,
      width: "100%",
      justifyContent: "flex-start",
    },
  },
  closeButton: {
    flexShrink: 0,
    padding: theme.spacing(0.75),
    [theme.breakpoints.down("sm")]: {
      gridColumn: 2,
      gridRow: 1,
    },
  },
  stageRail: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.25, 2),
    overflowX: "auto",
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: fade(theme.palette.background.default, 0.55),
    flexShrink: 0,
    margin: 0,
    listStyle: "none",
    ...theme.scrollbarStyles,
  },
  stageListItem: {
    minWidth: 132,
    display: "flex",
  },
  stageItem: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75, 1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
    textAlign: "left",
    transition: theme.transitions.create(["border-color", "background-color"]),
    "&:not(:disabled):hover": {
      borderColor: fade(theme.palette.primary.main, 0.55),
      backgroundColor: fade(theme.palette.primary.main, 0.045),
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    "&:disabled": {
      cursor: "default",
    },
  },
  activeStageItem: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.text.primary,
    backgroundColor: fade(theme.palette.primary.main, 0.08),
  },
  stageDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  stageName: {
    fontSize: "0.78rem",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  body: {
    display: "grid",
    gridTemplateColumns: "360px minmax(0, 1fr)",
    flex: 1,
    minHeight: 0,
    [theme.breakpoints.down("sm")]: {
      display: "block",
      overflowY: "auto",
    },
  },
  summaryPane: {
    minWidth: 0,
    overflowY: "auto",
    borderRight: `1px solid ${theme.palette.divider}`,
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      overflow: "visible",
      borderRight: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  workPane: {
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      overflow: "visible",
      minHeight: 480,
    },
  },
  empty: {
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
}));

const DealWorkspaceDialog = ({
  open,
  loading,
  title,
  status,
  stageName,
  stages,
  currentStageId,
  headerActions,
  summary,
  children,
  onClose,
  onStageChange,
  stageChangeDisabled,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      maxWidth={false}
      onClose={onClose}
      aria-labelledby="deal-workspace-title"
      aria-busy={loading}
      classes={{ paper: classes.paper }}
    >
      <Box className={classes.shell}>
        <Box className={classes.header}>
          <Box className={classes.identity}>
            <Typography className={classes.eyebrow}>Negocio</Typography>
            <Typography
              id="deal-workspace-title"
              variant="h6"
              className={classes.title}
            >
              {title || "Negocio"}
            </Typography>
            <Box className={classes.identityMeta}>
              {status}
              {stageName ? (
                <Typography variant="caption">{stageName}</Typography>
              ) : null}
            </Box>
          </Box>

          <Box className={classes.headerActions}>{headerActions}</Box>

          <IconButton
            className={classes.closeButton}
            aria-label="Fechar negocio"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {loading ? <LinearProgress aria-label="Atualizando negocio" /> : null}

        <Box
          component="ul"
          className={classes.stageRail}
          aria-label="Etapas do pipeline"
        >
          {(stages || []).map((stage) => {
            const active = Number(stage.id) === Number(currentStageId);

            return (
              <Box key={stage.id} component="li" className={classes.stageListItem}>
                <ButtonBase
                  component="button"
                  aria-current={active ? "step" : undefined}
                  aria-label={`${stage.name}${active ? ", etapa atual" : ""}`}
                  disabled={
                    active ||
                    stageChangeDisabled ||
                    typeof onStageChange !== "function"
                  }
                  onClick={() => onStageChange(stage.id)}
                  className={`${classes.stageItem} ${
                    active ? classes.activeStageItem : ""
                  }`}
                >
                  <span
                    className={classes.stageDot}
                    style={{ backgroundColor: stage.color || "#94a3b8" }}
                  />
                  <span className={classes.stageName}>{stage.name}</span>
                </ButtonBase>
              </Box>
            );
          })}
        </Box>

        {summary || children ? (
          <Box className={classes.body}>
            <Box className={classes.summaryPane}>{summary}</Box>
            <Box className={classes.workPane}>{children}</Box>
          </Box>
        ) : (
          <Box className={classes.empty}>Carregando negocio...</Box>
        )}
      </Box>
    </Dialog>
  );
};

export default DealWorkspaceDialog;
